import { useEffect, useState } from 'react';

import { env } from '@/env';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import {
  identitaDellaSessione,
  LIMULO_REALTIME_PATH,
  tagDaRileggereOf,
  tieniApertoIlFlusso,
} from '@/lib/limulo/realtime';
import { getLimuloToken, peekLimuloSession } from '@/lib/limulo/session';
import { limuloApi } from '@/services/limuloApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

/**
 * Il tempo reale di Limulo, montato UNA volta in `AppShell` (tma-orcb 08:20Z, P1): lo stream SSE del BFF,
 * e il suo unico evento per la TMA, `Tournament.action.changed`, che rilegge le azioni della barra e
 * l'incontro nominato (P2). La logica sta in `lib/limulo/realtime.ts`; qui c'e' il filo con Redux,
 * l'ambiente e la visibilita'.
 *
 * Quando e' aperto: `VITE_FEATURE_LIMULO_REALTIME_ENABLED` (solo `.env.staging`, P3) E la sezione Limulo
 * (flag di build, ruolo beta, sessione: lo stesso cancello di `AzionePersistente`) E l'app VISIBILE.
 *  · app in secondo piano ⇒ si chiude; al rientro si riapre, e dopo `ready` si rilegge (niente replay);
 *  · logout ⇒ `bridgeAuthenticated` cade e lo stream si chiude subito;
 *  · un'altra identita' a stream aperto (sessione nuova, altro account) ⇒ la vede il ciclo al frame
 *    successivo — al piu' un `heartbeat`, 25 s — e riapre col token nuovo.
 * ⚠️ Nessun polling tolto: le letture restano al loro ritmo, e il tempo reale le anticipa soltanto. Uno
 * stream giu' (rifiuto, rete, interruttore spento) lascia la TMA com'era, in silenzio.
 */
export function useTempoRealeLimulo(): void {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const isBeta = useHasLogtoRole('beta');
  const dispatch = useAppDispatch();
  const visibile = useAppVisibile();
  const acceso = env.featureLimuloRealtimeEnabled && env.featureLimuloEnabled && isBeta && authed && visibile;

  useEffect(() => {
    if (!acceso) return;
    const chiudi = new AbortController();
    void tieniApertoIlFlusso(
      {
        fetch: (url, init) => fetch(url, init),
        url: `${env.limuloApiUrl}${LIMULO_REALTIME_PATH}`,
        token: getLimuloToken,
        haSessione: () => peekLimuloSession() !== null,
        organizzazione: env.limuloOrgId,
        identita: () => identitaDellaSessione(peekLimuloSession()),
        attendi: (ms, signal) =>
          new Promise<void>((fatto) => {
            if (signal.aborted) {
              fatto();
              return;
            }
            const smetti = () => {
              clearTimeout(h);
              fatto();
            };
            const h = setTimeout(() => {
              signal.removeEventListener('abort', smetti);
              fatto();
            }, ms);
            signal.addEventListener('abort', smetti, { once: true });
          }),
        suAperto: () => {
          dispatch(limuloApi.util.invalidateTags(['LimuloAzioni']));
        },
        suAzioni: (evento) => {
          dispatch(limuloApi.util.invalidateTags(tagDaRileggereOf(evento)));
        },
      },
      chiudi.signal,
    ).catch(() => {
      // Il ciclo non lancia per costruzione; se un giorno lo facesse, la TMA resta al polling in silenzio
      // come per uno stream giu' — non un errore non gestito nella console di una WebView.
    });
    return () => chiudi.abort();
  }, [acceso, dispatch]);
}

/** L'app e' in primo piano? Una WebView nascosta non deve tenere aperta una connessione. */
function useAppVisibile(): boolean {
  const [visibile, setVisibile] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const su = () => setVisibile(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', su);
    return () => document.removeEventListener('visibilitychange', su);
  }, []);
  return visibile;
}
